import { ReactNode, useCallback, useEffect, useRef } from 'react';

import {
  FormFieldsHandle,
  FormFieldsProvider,
} from '../../context/FormFieldsContext';
import Content, { ContentProps } from './Content';
import SubmitError from './SubmitError';
import { useDialogContext } from './context';
import { DialogSubmitEvent } from './useDialogForm';

export type FormProps<
  T extends Record<string, unknown>,
  C extends Record<string, unknown> = Record<string, unknown>,
> = Omit<ContentProps, 'component' | 'onSubmit' | 'children'> & {
  initialValues: T;
  config?: C;
  onSubmit: (values: T) => void | Promise<void>;
  submitBehavior?: 'close' | 'manual';
  children: ReactNode;
};

export const Form = <
  T extends Record<string, unknown>,
  C extends Record<string, unknown> = Record<string, unknown>,
>({
  initialValues,
  config,
  onSubmit,
  submitBehavior = 'close',
  children,
  ...contentProps
}: FormProps<T, C>) => {
  const formRef = useRef<FormFieldsHandle<T, C>>(null);
  const { open } = useDialogContext();

  useEffect(() => {
    if (!open) {
      formRef.current?.resetForm();
    }
  }, [open]);

  const handleSubmit = useCallback(
    async (event: DialogSubmitEvent) => {
      const form = formRef.current;
      if (!form) {
        throw new Error('Form is not ready');
      }

      form.setShowErrors(true);
      if (!form.isValid) {
        event.preventDefault();
        return;
      }

      if (submitBehavior === 'manual') {
        event.preventDefault();
      }

      await onSubmit(form.values as T);
    },
    [onSubmit, submitBehavior],
  );

  return (
    <FormFieldsProvider<T, C>
      ref={formRef}
      initialValues={initialValues}
      config={config}
    >
      <Content component="form" onSubmit={handleSubmit} data-track-source-anchor="f9a2aa0ae5;form-content" {...contentProps}>
        <SubmitError />
        {children}
      </Content>
    </FormFieldsProvider>
  );
};

Form.displayName = 'DialogComposite.Form';
